// PublicPay — public unauthenticated invoice payment page.
//
// Routes:  /pay/:token           — payment form (Stripe PaymentElement)
//          /pay/:token/success   — post-checkout success page (polls status
//                                   until webhook flips the invoice to paid)
//
// SECURITY / PRIVACY NOTES:
//   • No Layout wrapper, no auth.  The signed :token in the URL is the ONLY
//     thing gating access — backend verifies it as an invoice_payment JWT
//     and cross-checks against invoices.stripe_pay_token, so a superseded or
//     expired link uniformly 404s.
//   • The backend response is already ruthlessly sanitized (invoice_number,
//     amounts, matter_title, firm name/logo, publishable key) — we render
//     only what it returns and never fetch anything else.
//   • The Stripe publishable key is safe by design (public identifier on
//     every stripe.js network request).  Secret keys NEVER touch this page.
//   • On confirmPayment success Stripe redirects to `return_url` on OUR
//     origin — we send the client to /pay/:token/success where we poll
//     GET /:token/status until our webhook handler flips invoice.status.
//     Stripe's redirect appends `payment_intent`, `payment_intent_client_secret`,
//     and `redirect_status` — we ignore them and rely on the status endpoint
//     as the source of truth (avoids trusting query-param values).
//   • "Powered by Stripe" attribution is required by Stripe's Standard Terms.
//
// TRUST-COMPLIANCE UX:
//   Retainer / trust-replenishment invoices should never generate a pay
//   token in the first place, but if the flag is set AFTER minting the token
//   the create-payment-intent endpoint returns 400 with a message about
//   contacting the firm — we surface that message verbatim.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import api from '../services/api'

// Cache Stripe.js loader promises PER publishable key across renders / page
// transitions.  loadStripe() memoizes internally but only for the lifetime
// of the module, and re-invoking with the same key is a no-op in the SDK —
// still, keeping a Map here avoids racing multiple loader promises when
// React StrictMode double-invokes effects.
const stripeCache = new Map()
function getStripe (key) {
  if (!key) return null
  if (!stripeCache.has(key)) stripeCache.set(key, loadStripe(key))
  return stripeCache.get(key)
}

// Format cents → "$1,234.56".  Backend also returns a pre-formatted string
// but we do our own currency formatting so the layout doesn't shift if the
// backend representation ever changes.
function formatUSD (cents) {
  const n = (Number(cents) || 0) / 100
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

// Shared minimal page shell — firm branding + Stripe attribution footer.
// Kept in one place so every state (loading / paid / error / etc.) renders
// consistent chrome without repeating the markup.
function PageShell ({ invoice, children }) {
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex-1 flex items-start justify-center p-4 sm:p-8">
        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
          <header className="px-6 sm:px-8 pt-6 pb-4 border-b border-gray-200 flex items-center gap-3">
            {invoice?.firm_logo_url
              ? <img src={invoice.firm_logo_url}
                     alt={invoice.firm_name || 'Firm logo'}
                     className="h-10 w-10 rounded object-contain bg-gray-50" />
              : <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center text-lg">⚖️</div>}
            <div className="min-w-0">
              <div className="text-sm text-gray-500">Secure payment</div>
              <div className="font-semibold text-gray-900 truncate">
                {invoice?.firm_name || 'Your law firm'}
              </div>
            </div>
          </header>
          <div className="px-6 sm:px-8 py-6">{children}</div>
        </div>
      </div>
      <footer className="text-center text-xs text-gray-500 pb-6 px-4">
        Powered by{' '}
        <a href="https://stripe.com" target="_blank" rel="noopener noreferrer"
           className="underline hover:text-gray-700">Stripe</a>.
        Your card details never touch our servers.
      </footer>
    </div>
  )
}

function InvoiceSummary ({ invoice }) {
  if (!invoice) return null
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Invoice</div>
      <div className="font-semibold text-gray-900 text-lg">{invoice.invoice_number}</div>
      {invoice.matter_title && (
        <div className="text-sm text-gray-700 mt-1">{invoice.matter_title}</div>
      )}
      <div className="mt-3 pt-3 border-t border-gray-200 flex items-baseline justify-between">
        <span className="text-sm text-gray-600">Amount due</span>
        <span className="text-2xl font-semibold text-gray-900">
          {formatUSD(invoice.balance_due_cents)}
        </span>
      </div>
      {Number(invoice.total_cents) !== Number(invoice.balance_due_cents) && (
        <div className="mt-1 flex items-baseline justify-between text-xs text-gray-500">
          <span>Original invoice total</span>
          <span>{formatUSD(invoice.total_cents)}</span>
        </div>
      )}
    </div>
  )
}

// Inner form — must be a descendant of <Elements> so useStripe/useElements
// have access to the Stripe context.  On submit we call confirmPayment which
// (a) validates the PaymentElement, (b) posts card details DIRECTLY to
// Stripe (never our server), (c) redirects to return_url on success.
function CheckoutForm ({ token, invoice }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setErrorMsg('')
    setSubmitting(true)
    // Wave 6 finding #6 — return URL puts token in fragment (never path/query).
    const returnUrl = `${window.location.origin}/pay/success#t=${encodeURIComponent(token)}`
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    })
    // If we get here without a redirect, confirmPayment failed synchronously
    // (validation error, immediate decline).  Success cases redirect away.
    if (result.error) {
      // Stripe surfaces user-facing messages on validation_error / card_error
      // types.  Anything else is a system error we should keep generic.
      const msg = (result.error.type === 'validation_error' || result.error.type === 'card_error')
        ? (result.error.message || 'Payment failed. Please check your card details.')
        : 'Payment could not be completed. Please try again or contact your law firm.'
      setErrorMsg(msg)
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {errorMsg && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {errorMsg}
        </div>
      )}
      <button type="submit"
              disabled={!stripe || submitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium px-4 py-3 rounded-lg text-sm transition-colors">
        {submitting ? 'Processing…' : `Pay ${formatUSD(invoice.balance_due_cents)}`}
      </button>
      <p className="text-xs text-gray-500 text-center">
        Your card is charged securely by Stripe. Card details never touch{' '}
        {invoice.firm_name || 'the firm'}'s servers.
      </p>
    </form>
  )
}

// Wave 6 finding #6 — read the pay token from URL fragment (#t=…) OR the
// legacy path param.  Fragment is preferred (never leaks to logs/history/
// Referer).  On first mount we persist to sessionStorage and
// history.replaceState-scrub the hash so page reloads still work.
function _resolvePayToken (params, location) {
  const SS_KEY = 'lextrack_pay_token'
  // Path-param variant (legacy pre-fix links) takes priority so we don't
  // pick up a stale sessionStorage token when someone opens a different
  // invoice link in the same tab.
  if (params.token && typeof params.token === 'string' && params.token.length > 0) {
    try { window.sessionStorage.setItem(SS_KEY, params.token) } catch {}
    return params.token
  }
  const hash = String(location.hash || '')
  if (hash.startsWith('#')) {
    const p = new URLSearchParams(hash.slice(1))
    const t = p.get('t')
    if (t && t.length > 0) {
      try { window.sessionStorage.setItem(SS_KEY, t) } catch {}
      // Scrub the hash so a reload / bookmark / screenshot doesn't leak it.
      try {
        const clean = location.pathname + location.search
        window.history.replaceState(null, '', clean)
      } catch {}
      return t
    }
  }
  // Restore from sessionStorage on refresh (hash already scrubbed).
  try {
    const cached = window.sessionStorage.getItem(SS_KEY)
    if (cached) return cached
  } catch {}
  return ''
}

export default function PublicPay () {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const isSuccess = location.pathname.endsWith('/success')

  // Resolved once per mount; the location.hash dep would fire us every time
  // history.replaceState runs (during our own scrub), so pin to pathname.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const token = useMemo(() => _resolvePayToken(params, location), [params.token, location.pathname])

  const [state, setState] = useState('loading')
  // loading | ready | paid | already_paid | uncollectible | not_yet_ready
  // | notfound | processing | processing_timeout | payments_disabled | error
  const [invoice, setInvoice] = useState(null)
  const [clientSecret, setClientSecret] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  // Fetch invoice metadata + (on pay route) mint a PaymentIntent so the
  // Elements provider has a client_secret from the very first render.  The
  // /success route skips the intent step and jumps straight to polling.
  useEffect(() => {
    let cancelled = false
    setState('loading')
    setErrorMsg('')
    if (!token) {
      setState('notfound')
      return () => { cancelled = true }
    }
    ;(async () => {
      try {
        const r = await api.get(`/api/public/pay/${encodeURIComponent(token)}`)
        if (cancelled) return
        const inv = r.data
        setInvoice(inv)
        document.title = `Pay ${inv.invoice_number || 'invoice'} · ${inv.firm_name || 'Legal services'}`

        // Terminal states — never mint an intent for these.
        if (inv.status === 'paid') { setState(isSuccess ? 'paid' : 'already_paid'); return }
        if (inv.status === 'void' || inv.status === 'refunded') { setState('uncollectible'); return }
        if (inv.status === 'draft') { setState('not_yet_ready'); return }

        if (isSuccess) {
          // Skip intent creation; polling below flips the invoice to paid.
          setState('processing')
          return
        }

        // Mint a PaymentIntent.  The endpoint's own guards will 400 if the
        // firm hasn't finished Stripe setup / disabled payments / the
        // invoice is retainer replenishment / the balance is under 50¢.
        try {
          const intent = await api.post(`/api/public/pay/${encodeURIComponent(token)}/create-payment-intent`)
          if (cancelled) return
          setClientSecret(intent.data.client_secret || '')
          setState('ready')
        } catch (err) {
          if (cancelled) return
          const body = err.response?.data || {}
          const msg = body.message || body.error || 'Could not initialize payment.'
          // 400 from create-payment-intent = firm-side config / trust-compliance
          // / disabled — treat as a soft "call the firm" state.
          setErrorMsg(msg)
          setState(err.response?.status === 400 ? 'payments_disabled' : 'error')
        }
      } catch (err) {
        if (cancelled) return
        // Backend uniformly returns 404 {error: 'Not found'} for expired /
        // tampered / superseded tokens (deliberate no-oracle security
        // posture — see publicPay.js header).  The frontend can't
        // distinguish expired from not-found either.  Best UX is a single
        // "not found — ask firm for a new link" state.  Wave 6 finding
        // #11 / #36.
        if (err.response?.status === 404) setState('notfound')
        else if (err.response?.status === 429) {
          setErrorMsg(err.response?.data?.message || 'Too many requests. Please wait a moment and try again.')
          setState('error')
        }
        else { setErrorMsg(err.response?.data?.error || 'Could not load invoice.'); setState('error') }
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isSuccess])

  // Success-page polling — the webhook usually fires within 1-3 seconds.
  // We poll every 2s for up to 30s (~15 attempts).  A slower webhook won't
  // deadlock the UI — we show a "still processing" message with a manual
  // refresh option after the timeout.
  useEffect(() => {
    if (!isSuccess || state !== 'processing') return
    let cancelled = false
    let attempts = 0
    const MAX_ATTEMPTS = 15
    let intervalMs = 2000

    const tick = async () => {
      if (cancelled) return
      attempts += 1
      try {
        const r = await api.get(`/api/public/pay/${encodeURIComponent(token)}/status`)
        if (cancelled) return
        // Merge status into invoice so the summary reflects the new balance.
        setInvoice((prev) => prev ? { ...prev, ...r.data } : prev)
        if (r.data.status === 'paid') {
          setState('paid')
          return
        }
        if (r.data.status === 'void' || r.data.status === 'refunded') {
          setState('uncollectible')
          return
        }
        // Reset interval on a healthy response after prior backoff.
        intervalMs = 2000
      } catch (err) {
        // 404 mid-poll = token invalidated by admin action.  Fall through
        // to timeout handling; the user can refresh if they think it's a blip.
        if (err.response?.status === 404) {
          setState('processing_timeout')
          return
        }
        // Wave 6 finding #33 — respect rate-limit backoff so a slow webhook
        // + user refresh doesn't burn the budget and strand a paid customer
        // on an error screen.  Server returns retry_after_seconds; honor it.
        if (err.response?.status === 429) {
          const retry = Number(err.response?.data?.retry_after_seconds) || 5
          intervalMs = Math.min(30000, Math.max(intervalMs, retry * 1000))
          // Don't count this attempt against MAX — it was a throttle, not a
          // real webhook-latency data point.
          attempts = Math.max(0, attempts - 1)
        }
        // Other errors — just keep trying, network flap etc.
      }
      if (attempts >= MAX_ATTEMPTS) { setState('processing_timeout'); return }
      setTimeout(tick, intervalMs)
    }
    // First poll fires immediately; subsequent every 2s.
    tick()
    return () => { cancelled = true }
  }, [isSuccess, state, token])

  // Memoize Elements options so the provider doesn't tear down + rebuild
  // its iframe on every re-render (which would blow away entered card data).
  const stripePromise = useMemo(
    () => getStripe(invoice?.stripe_publishable_key),
    [invoice?.stripe_publishable_key]
  )
  const elementsOptions = useMemo(() => (
    clientSecret ? { clientSecret, appearance: { theme: 'stripe' } } : null
  ), [clientSecret])

  const goBack = useCallback(() => {
    // Fragment-first URL — token stays out of history.
    navigate(`/pay#t=${encodeURIComponent(token)}`, { replace: true })
  }, [navigate, token])

  // ─── Render branches ─────────────────────────────────────────────────

  if (state === 'loading') {
    return (
      <PageShell invoice={invoice}>
        <div className="text-sm text-gray-600 py-8 text-center">Loading invoice…</div>
      </PageShell>
    )
  }

  if (state === 'notfound') {
    return (
      <PageShell invoice={invoice}>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          Payment link not found
        </h1>
        <p className="text-sm text-gray-700">
          This payment link is no longer valid — it may have expired, been
          replaced, or been fully paid. Contact{' '}
          <strong>{invoice?.firm_name || 'your law firm'}</strong> to request a new link.
        </p>
      </PageShell>
    )
  }

  if (state === 'paid' || state === 'already_paid') {
    return (
      <PageShell invoice={invoice}>
        <div className="text-center py-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-2xl mb-4">✓</div>
          <h1 className="text-lg font-semibold text-gray-900 mb-1">
            {state === 'paid' ? 'Payment received — thank you!' : 'This invoice has already been paid'}
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            {state === 'paid'
              ? `Your payment for invoice ${invoice?.invoice_number} has been received.`
              : `Thank you — invoice ${invoice?.invoice_number} was previously paid in full.`}
          </p>
          {invoice && (
            <div className="text-left">
              <InvoiceSummary invoice={{ ...invoice, balance_due_cents: 0 }} />
            </div>
          )}
          <p className="text-xs text-gray-500 mt-4">
            A payment confirmation will appear on your bank or card statement as{' '}
            <strong>{invoice?.firm_name || 'the firm'}</strong>. Save this page for your records.
          </p>
        </div>
      </PageShell>
    )
  }

  if (state === 'processing') {
    return (
      <PageShell invoice={invoice}>
        <div className="text-center py-6">
          <div className="mx-auto w-14 h-14 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin mb-4" />
          <h1 className="text-lg font-semibold text-gray-900 mb-1">Confirming your payment…</h1>
          <p className="text-sm text-gray-600">
            Stripe has received your card details. We're waiting for the final
            confirmation from your bank — this usually takes just a few seconds.
          </p>
        </div>
      </PageShell>
    )
  }

  if (state === 'processing_timeout') {
    return (
      <PageShell invoice={invoice}>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Payment is still processing</h1>
        <p className="text-sm text-gray-700 mb-4">
          Your card may have been charged. We haven't yet received the final
          confirmation from Stripe, which occasionally takes longer than expected.
          Do <strong>not</strong> submit another payment.
        </p>
        <p className="text-sm text-gray-700 mb-4">
          Contact <strong>{invoice?.firm_name || 'your law firm'}</strong> to confirm
          receipt, or refresh this page in a few minutes.
        </p>
        <button onClick={() => window.location.reload()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-4 py-2 rounded-lg text-sm">
          Refresh status
        </button>
      </PageShell>
    )
  }

  if (state === 'uncollectible') {
    return (
      <PageShell invoice={invoice}>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">This invoice is no longer collectible</h1>
        <p className="text-sm text-gray-700">
          Invoice {invoice?.invoice_number} has been {invoice?.status === 'refunded' ? 'refunded' : 'voided'}.
          Contact <strong>{invoice?.firm_name || 'your law firm'}</strong> if you have questions.
        </p>
      </PageShell>
    )
  }

  if (state === 'not_yet_ready') {
    return (
      <PageShell invoice={invoice}>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">This invoice isn't ready for payment yet</h1>
        <p className="text-sm text-gray-700">
          Invoice {invoice?.invoice_number} is still a draft.  Please wait for a
          final invoice from <strong>{invoice?.firm_name || 'your law firm'}</strong>.
        </p>
      </PageShell>
    )
  }

  if (state === 'payments_disabled') {
    return (
      <PageShell invoice={invoice}>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Online payment isn't available for this invoice</h1>
        {invoice && <InvoiceSummary invoice={invoice} />}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          {errorMsg || 'Contact your law firm to arrange payment.'}
        </div>
      </PageShell>
    )
  }

  if (state === 'error') {
    return (
      <PageShell invoice={invoice}>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-4">
          {errorMsg || 'Please try again in a minute.'}
        </div>
        <button onClick={() => window.location.reload()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-4 py-2 rounded-lg text-sm">
          Try again
        </button>
      </PageShell>
    )
  }

  // state === 'ready' — render the Stripe checkout.
  return (
    <PageShell invoice={invoice}>
      <h1 className="text-lg font-semibold text-gray-900 mb-4">Pay invoice</h1>
      <InvoiceSummary invoice={invoice} />
      {(!stripePromise || !elementsOptions) ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          Payment is temporarily unavailable. Please refresh, or contact{' '}
          <strong>{invoice?.firm_name || 'your law firm'}</strong>.
        </div>
      ) : (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <CheckoutForm token={token} invoice={invoice} />
        </Elements>
      )}
      {location.pathname.endsWith('/success') && (
        // Defensive: shouldn't be reachable, /success routes never land on
        // 'ready' state — but if the user navigates back from success we
        // give them a link to restart cleanly.
        <button onClick={goBack} className="mt-4 text-xs text-gray-500 hover:text-gray-700 underline">
          ← Restart payment
        </button>
      )}
    </PageShell>
  )
}
